// ABOUTME: Fetches + live-refreshes a surface's crossings across the three auth modes
// ABOUTME: (parent, parent-multi/tablet, child), windowing open vs done-today (Issue #1939).

import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { useSignalR } from '../contexts/SignalRContext';
import { getTodayInFamilyTimezone } from '../utils/dateUtils';
import type { CrossingListItem } from '../types/api';

export type CrossingsDescriptor =
  | { mode: 'parent'; childId: number; familyTimezone?: string }
  | { mode: 'parent-multi'; childIds: number[]; familyTimezone?: string }
  | { mode: 'child'; slug: string; token?: string; familyTimezone?: string };

export interface CrossingListItemWithChild extends CrossingListItem {
  childId: number;
}

const OPEN_STATUSES = ['NotStarted', 'InProgress'];
const DONE_STATUSES = ['Complete'];

// Axios's default paramsSerializer renders an array param as repeated bracketed keys
// (status[]=NotStarted&status[]=InProgress), which ASP.NET Core's default List<T> query-string
// binder does NOT understand -- it silently binds to null, skipping the filter entirely. That
// made the "done-today" query return every status (not just Complete) within the date range,
// duplicating any NotStarted/InProgress crossing already returned by the open query. Serialize
// repeated plain keys (status=NotStarted&status=InProgress) instead, matching what
// [FromQuery(Name = "status")] List<CrossingStatus>? actually binds.
function serializeRepeatedParams(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach(v => usp.append(key, String(v)));
    } else {
      usp.append(key, String(value));
    }
  });
  return usp.toString();
}

async function fetchForChild(childId: number, today: string): Promise<CrossingListItemWithChild[]> {
  const [open, done] = await Promise.all([
    api.get<CrossingListItem[]>(`/api/children/${childId}/crossings`, {
      params: { status: OPEN_STATUSES },
      paramsSerializer: serializeRepeatedParams,
    }),
    api.get<CrossingListItem[]>(`/api/children/${childId}/crossings`, {
      params: { status: DONE_STATUSES, from: today, to: today },
      paramsSerializer: serializeRepeatedParams,
    }),
  ]);
  return [...open.data, ...done.data].map(item => ({ ...item, childId }));
}

async function fetchForChildSelf(slug: string, token: string | undefined, today: string): Promise<CrossingListItemWithChild[]> {
  const baseParams = token ? { token } : {};
  const [open, done] = await Promise.all([
    api.get<CrossingListItem[]>(`/api/child/${slug}/crossings`, {
      params: { ...baseParams, status: OPEN_STATUSES },
      paramsSerializer: serializeRepeatedParams,
    }),
    api.get<CrossingListItem[]>(`/api/child/${slug}/crossings`, {
      params: { ...baseParams, status: DONE_STATUSES, from: today, to: today },
      paramsSerializer: serializeRepeatedParams,
    }),
  ]);
  // childId isn't on the wire DTO for the child surface (the endpoint is already child-scoped);
  // 0 is a safe placeholder since child-mode consumers never group by childId.
  return [...open.data, ...done.data].map(item => ({ ...item, childId: 0 }));
}

export function useCrossings(descriptor: CrossingsDescriptor) {
  const [items, setItems] = useState<CrossingListItemWithChild[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { on } = useSignalR();

  const fetchAll = useCallback(async () => {
    const today = getTodayInFamilyTimezone(descriptor.familyTimezone);
    try {
      setIsLoading(true);
      let merged: CrossingListItemWithChild[];
      if (descriptor.mode === 'parent') {
        merged = await fetchForChild(descriptor.childId, today);
      } else if (descriptor.mode === 'parent-multi') {
        const results = await Promise.all(descriptor.childIds.map(id => fetchForChild(id, today)));
        merged = results.flat();
      } else {
        merged = await fetchForChildSelf(descriptor.slug, descriptor.token, today);
      }
      setItems(merged);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch crossings'));
    } finally {
      setIsLoading(false);
    }
    // descriptor fields are primitives/arrays; stringify to keep the effect stable across re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(descriptor)]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const unsubscribe = on('CrossingUpdated', () => {
      fetchAll();
    });
    return unsubscribe;
  }, [on, fetchAll]);

  return { items, isLoading, error, refetch: fetchAll };
}
